import { OrganizationInsertDto } from '../api/types/dtos/organization.dto';

export type FieldType = 
  | 'string'
  | 'number'
  | 'boolean'
  | 'uuid'
  | 'string[]'
  | 'object_map_string_number' // For crops: Record<string, number>
  | 'percentage' // Example for future use, maps to number
  | 'currency';  // Example for future use, maps to number

export type FieldParser = 'none' | 'multi_column' | 'coordinate_split' | 'crops';

export interface FieldMetadata {
  key: keyof OrganizationInsertDto;
  displayName: string;
  type: FieldType;
  requiredApi: boolean;
  supportsDefault: boolean;
  allowCustomInputForSelect?: boolean;
  selectOptions?: { value: string; label: string }[];
  helperText?: string;
  stringArraySeparator?: string;
  parser?: FieldParser;
  hidden?: boolean;
  defaultValue?: unknown;
  linkedField?: keyof OrganizationInsertDto;
}

export const ORGANIZATION_FIELDS_METADATA: FieldMetadata[] = [
  { key: 'name', displayName: 'Nombre de la Finca', type: 'string', requiredApi: true, supportsDefault: false, parser: 'none' },
  {
    key: 'idTypeId',
    displayName: 'Tipo de ID',
    type: 'string',
    requiredApi: true,
    supportsDefault: true,
    allowCustomInputForSelect: true,
    selectOptions: [
      { value: 'CC', label: 'Cédula de Ciudadanía (CC)' },
      { value: 'NIT', label: 'Número de Identificación Tributaria (NIT)' },
      { value: 'CE', label: 'Cédula de Extranjería (CE)' },
    ],
    helperText: 'Selecciona un tipo de ID o elige "Personalizado" para ingresar uno especifico.',
    parser: 'none',
  },
  { key: 'idNumber', displayName: 'Codigo de Finca', type: 'string', requiredApi: true, supportsDefault: false, parser: 'none' },
  { key: 'logo', displayName: 'URL del Logo', type: 'string', requiredApi: false, supportsDefault: true, parser: 'none' },
  { key: 'isActive', displayName: 'Activa', type: 'boolean', requiredApi: false, supportsDefault: true, helperText: 'Por defecto verdadero.', parser: 'none', defaultValue: true },
  { key: 'isMain', displayName: 'Es Organizacion Principal', type: 'boolean', requiredApi: false, supportsDefault: true, helperText: 'Siempre falso para importacion por lotes.', parser: 'none', hidden: true, defaultValue: false },
  { key: 'isFarm', displayName: 'Es Finca', type: 'boolean', requiredApi: false, supportsDefault: true, helperText: 'Por defecto verdadero.', parser: 'none', defaultValue: true },
  { key: 'ownerName', displayName: 'Primer Nombre del Propietario', type: 'string', requiredApi: true, supportsDefault: false, parser: 'none' },
  {
    key: 'ownerIdTypeId',
    displayName: 'Tipo de ID del Propietario',
    type: 'string',
    requiredApi: true,
    supportsDefault: true,
    allowCustomInputForSelect: true,
    selectOptions: [
        { value: 'CC', label: 'Cédula de Ciudadanía (CC)' },
        { value: 'NIT', label: 'Número de Identificación Tributaria (NIT)' },
        { value: 'CE', label: 'Cédula de Extranjería (CE)' },
    ],
    helperText: 'Selecciona el tipo de ID del propietario o elige "Personalizado".',
    parser: 'none',
  },
  { key: 'ownerIdNumber', displayName: 'Documento del Propietario', type: 'string', requiredApi: true, supportsDefault: false, parser: 'none' },
  { key: 'ownerLastName', displayName: 'Apellido del Propietario', type: 'string', requiredApi: false, supportsDefault: true, parser: 'none' },
  { key: 'ownerMiddleName', displayName: 'Segundo Nombre del Propietario', type: 'string', requiredApi: false, supportsDefault: true, parser: 'none' },
  { key: 'ownerNickName', displayName: 'Apodo del Propietario', type: 'string', requiredApi: false, supportsDefault: true, parser: 'none' },
  {
    key: 'ownerGender',
    displayName: 'Genero del Propietario',
    type: 'string',
    requiredApi: false,
    supportsDefault: true,
    allowCustomInputForSelect: true,
    selectOptions: [
      { value: 'M', label: 'Masculino' },
      { value: 'F', label: 'Femenino' },
      { value: 'O', label: 'Otro' },
    ],
    parser: 'none',
  },
  { key: 'ownerBirthdate', displayName: 'Fecha de Nacimiento del Propietario', type: 'string', requiredApi: false, supportsDefault: true, helperText: 'Formato: AAAA-MM-DD', parser: 'none' },
  { key: 'ownerEmail', displayName: 'Correo del Propietario', type: 'string', requiredApi: false, supportsDefault: true, parser: 'none' },
  { key: 'ownerAddress', displayName: 'Direccion del Propietario', type: 'string', requiredApi: false, supportsDefault: true, parser: 'none' },
  {
    key: 'ownerPhoneNumbers',
    displayName: 'Telefonos del Propietario',
    type: 'string[]',
    requiredApi: false,
    supportsDefault: false,
    stringArraySeparator: ',',
    helperText: 'Selecciona una o mas columnas. Los valores se combinaran en una lista de telefonos.',
    parser: 'multi_column',
  },
  { key: 'cityId', displayName: 'Ciudad', type: 'string', requiredApi: false, supportsDefault: true, helperText: 'Nombre o UUID.', parser: 'none' },
  { key: 'regionId', displayName: 'Region/Departamento', type: 'string', requiredApi: false, supportsDefault: true, helperText: 'Nombre o UUID.', parser: 'none' },
  { key: 'countryId', displayName: 'Pais', type: 'string', requiredApi: true, supportsDefault: true, helperText: 'Nombre o UUID.', parser: 'none', defaultValue: 'Colombia' },
  { key: 'corregimientoId', displayName: 'Corregimiento', type: 'string', requiredApi: false, supportsDefault: true, helperText: 'Nombre o UUID.', parser: 'none' },
  { key: 'veredaId', displayName: 'Vereda', type: 'string', requiredApi: false, supportsDefault: true, helperText: 'Nombre o UUID.', parser: 'none' },
  {
    key: 'latitude',
    displayName: 'Latitud',
    type: 'number',
    requiredApi: false,
    supportsDefault: true,
    parser: 'coordinate_split',
    linkedField: 'longitude',
  },
  {
    key: 'longitude',
    displayName: 'Longitud',
    type: 'number',
    requiredApi: false,
    supportsDefault: true,
    parser: 'coordinate_split',
    linkedField: 'latitude',
  },
  { key: 'elevation', displayName: 'Elevacion (metros)', type: 'number', requiredApi: false, supportsDefault: true, parser: 'none' },
  { key: 'geometry', displayName: 'Geometria de la Finca (GeoJSON)', type: 'string', requiredApi: false, supportsDefault: true, helperText: 'Cadena GeoJSON.', parser: 'none' },
  { key: 'area', displayName: 'Area Total (hectareas)', type: 'number', requiredApi: false, supportsDefault: true, parser: 'none' },
  { key: 'malesFullTimeWorkers', displayName: 'Hombres Tiempo Completo', type: 'number', requiredApi: false, supportsDefault: true, parser: 'none' },
  { key: 'malesTemporalWorkers', displayName: 'Hombres Temporales', type: 'number', requiredApi: false, supportsDefault: true, parser: 'none' },
  { key: 'femaleFullTimeWorkers', displayName: 'Mujeres Tiempo Completo', type: 'number', requiredApi: false, supportsDefault: true, parser: 'none' },
  { key: 'femaleTemporalWorkers', displayName: 'Mujeres Temporales', type: 'number', requiredApi: false, supportsDefault: true, parser: 'none' },
  {
    key: 'crops',
    displayName: 'Cultivos',
    type: 'object_map_string_number',
    requiredApi: false,
    supportsDefault: false,
    helperText: 'Selecciona columnas de cultivos. Encabezado = nombre del cultivo, valor = area.',
    parser: 'crops',
  },
  {
    key: 'groupIds',
    displayName: 'Grupos',
    type: 'string[]',
    requiredApi: false,
    supportsDefault: true,
    helperText: 'Selecciona una o mas columnas de grupo. Los valores se combinan en una lista separada por comas.',
    parser: 'multi_column',
    stringArraySeparator: ',',
  },
]; 